const qListeners = getPropCacheMixin('$listeners', 'qListeners');

export default {
  name: 'ExpansionItem',
  mixins: [qListeners],
  props: {
    value: {
      type: Boolean,
      default: undefined,
    },
    defaultOpened: Boolean,
    label: {
      type: String,
      default: '',
    },
    additionalLabel: {
      type: String,
      default: '',
    },
    content: {
      type: String,
      default: '',
    },
    disable: Boolean,
    group: {
      type: String,
      default: null,
    },
    duration: {
      type: Number,
      default: 300,
    },
    number: {
      type: [Number, String],
      default: null,
    },
    type: {
      type: String,
      default: 'primary',
      validator: (value) => {
        return ['primary', 'secondary'].indexOf(value) !== -1;
      },
    },
  },
  data() {
    return {
      showing: this.value !== void 0
        ? this.value
        : this.defaultOpened,
    };
  },
  computed: {
    showingClass() {
      return `expansion-item_${this.showing === true ? 'expanded' : 'collapsed'}`;
    },
    typeClass() {
      return `expansion-item--${this.type}`;
    },
  },
  watch: {
    showing(val) {
      val === true && this.group !== void 0 && this.$root.$emit('expansionItem:close', this);
    },
    group(newVal, oldVal) {
      if (newVal !== void 0 && oldVal === void 0) {
        this.$root.$on('expansionItem:close', this.__eventHandler);
      } else if (newVal === void 0 && oldVal !== void 0) {
        this.$root.$off('expansionItem:close', this.__eventHandler);
      }
    },
    value(val) {
      this.__processModelChange(val);
    },
  },
  created() {
    this.group !== void 0 && this.$root.$on('expansionItem:close', this.__eventHandler);
  },
  beforeDestroy() {
    this.__tickFn = void 0;
    this.animating && this.__cleanup();
    this.group !== void 0 && this.$root.$off('expansionItem:close', this.__eventHandler);
  },
  methods: {
    __begin(el, height, done) {
      el.style.overflowY = 'hidden';
      if (height !== void 0) {
        el.style.height = `${height}px`;
      }
      el.style.transition = `height ${this.duration}ms cubic-bezier(.25, .8, .50, 1)`;

      this.animating = true;
      this.done = done;
    },
    __end(el, event) {
      el.style.overflowY = null;
      el.style.height = null;
      el.style.transition = null;
      this.__cleanup();
      event !== this.lastEvent && this.$emit(event);
    },
    __cleanup() {
      this.done && this.done();
      this.done = null;
      this.animating = false;

      clearTimeout(this.timer);
      clearTimeout(this.timerFallback);
      this.el !== void 0 && this.el.removeEventListener('transitionend', this.animListener);
      this.animListener = null;
    },
    enter(el, done) {
      let pos = 0;
      this.el = el;

      if (this.animating === true) {
        pos = el.offsetHeight === el.scrollHeight ? 0 : void 0;
      } else {
        this.lastEvent = 'hide';
      }

      this.__begin(el, pos, done);

      this.timer = setTimeout(() => {
        el.style.height = `${el.scrollHeight}px`;
        this.animListener = ev => {
          if (Object(ev) !== ev || ev.target === el) {
            this.__end(el, 'show');
          }
        };
        el.addEventListener('transitionend', this.animListener);
        this.timerFallback = setTimeout(this.animListener, this.duration * 1.1);
      }, 100);
    },
    leave(el, done) {
      let pos;
      this.el = el;

      if (this.animating === true) {
        this.__cleanup();
      } else {
        this.lastEvent = 'show';
        pos = el.scrollHeight;
      }

      this.__begin(el, pos, done);

      this.timer = setTimeout(() => {
        el.style.height = 0;
        this.animListener = ev => {
          if (Object(ev) !== ev || ev.target === el) {
            this.__end(el, 'hide');
          }
        };
        el.addEventListener('transitionend', this.animListener);
        this.timerFallback = setTimeout(this.animListener, this.duration * 1.1);
      }, 100);
    },
    __prepareTick() {
      if (this.__tickFn !== void 0) {
        const fn = this.__tickFn;
        this.$nextTick(() => {
          if (this.__tickFn === fn) {
            this.__tickFn();
            this.__tickFn = void 0;
          }
        });
      }
    },
    __clearTick() {
      this.__tickFn = void 0;
    },
    toggle(evt) {
      this[this.showing === true ? 'hide' : 'show'](evt);
    },
    show(evt) {
      if (this.disable === true || (this.__showCondition !== void 0 && this.__showCondition(evt) !== true)) {
        return;
      }

      if (qListeners.input !== void 0) {
        this.$emit('input', true);
        this.payload = evt;
        this.$nextTick(() => {
          if (this.payload === evt) {
            this.payload = void 0;
          }
        });
      }

      if (this.value === void 0 || qListeners.input === void 0) {
        this.__processShow(evt);
      }
    },
    __processShow(evt) {
      if (this.showing === true) {
        return;
      }

      this.__preparePortal !== void 0 && this.__preparePortal();

      this.showing = true;

      this.$emit('before-show', evt);
      this.$emit('show', evt);
    },
    hide(evt) {
      if (this.disable === true) {
        return;
      }

      if (qListeners.input !== void 0) {
        this.$emit('input', false);
        this.payload = evt;
        this.$nextTick(() => {
          if (this.payload === evt) {
            this.payload = void 0;
          }
        });
      }
      if (this.value === void 0 || qListeners.input === void 0) {
        this.__processHide(evt);
      }
    },
    __processHide(evt) {
      if (this.showing === false) {
        return;
      }

      this.showing = false;

      this.$emit('before-hide', evt);

      if (this.__hide !== void 0) {
        this.__clearTick();
        this.__hide(evt);
        this.__prepareTick();
      } else {
        this.$emit('hide', evt);
      }
    },
    __processModelChange(val) {
      if (this.disable === true && val === true) {
        qListeners.input !== void 0 && this.$emit('input', false);
      } else if ((val === true) !== this.showing) {
        this[`__process${val === true ? 'Show' : 'Hide'}`](this.payload);
      }
    },
    __onHeaderClick(e) {
      e.stopPropagation();
      this.hasRouterLink !== true && this.toggle(e);
      this.$emit('click', e);
    },
    __onHeaderKeyup(e) {
      if (e.key !== 'Enter') return;
      this.__onHeaderClick(e);
    },
    __eventHandler(comp) {
      this !== comp && this.group === comp.group && this.hide();
    },
  },
};

function getPropCacheMixin(propName, proxyPropName) {
  return {
    data() {
      const target = {};
      const source = this[propName];

      for (const prop in source) {
        target[prop] = source[prop];
      }

      return { [proxyPropName]: target };
    },

    watch: {
      [propName](newObj, oldObj) {
        const target = this[proxyPropName];

        if (oldObj !== void 0) {
          for (const prop in oldObj) {
            if (newObj[prop] === void 0) {
              this.$delete(target, prop);
            }
          }
        }

        for (const prop in newObj) {
          if (target[prop] !== newObj[prop]) {
            this.$set(target, prop, newObj[prop]);
          }
        }
      },
    },
  };
}
